import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { devWarn } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = unauthenticated, object = user
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrg(data.organization || null);
      return data.user;
    } catch (err) {
      // Cookie missing/expired or network error — treat as unauthenticated.
      // We deliberately keep this quiet (the app renders the login screen).
      if (err?.response?.status && err.response.status !== 401) {
        devWarn("/auth/me failed:", err?.message || err);
      }
      setUser(false);
      setOrg(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    if (data.user.org_id) {
      const orgResp = await api.get(`/organizations/${data.user.org_id}`).catch((err) => {
        devWarn("Org fetch failed after login:", err?.message || err);
        return null;
      });
      setOrg(orgResp?.data || null);
    } else {
      setOrg(null);
    }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      devWarn("/auth/logout failed (session cleared locally anyway):", err?.message || err);
    }
    setUser(false);
    setOrg(null);
  }, []);

  const hasPerm = useCallback(
    (p) => {
      if (!user) return false;
      if (user.role === "super_admin") return true;
      return (user.permissions || []).includes(p);
    },
    [user],
  );

  // Memoized to avoid a new object identity on every render — prevents
  // needless re-renders in every consumer of useAuth().
  const value = useMemo(
    () => ({ user, org, login, logout, refresh, setUser, hasPerm }),
    [user, org, login, logout, refresh, hasPerm],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
